import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Button, Card, ErrorText, Field, Modal, Select, Spinner, Table, EmptyState, Input } from '../../components/ui'
import { clp, formatRut } from '../../utils/format'
import type { Battery, Company, Patient } from '../../types/api'
import { Icon } from '../../components/ui/icons'

interface Order {
  id: string
  status: 'CREATED' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
  result: 'PENDING' | 'FIT' | 'FIT_WITH_RESTRICTIONS' | 'UNFIT'
  jobTitle?: string | null
  certificateCode?: string | null
  payerType: 'COMPANY' | 'PATIENT'
  patient: { id: string; rut: string; firstName: string; lastName: string }
  battery: { id: string; name: string; basePrice: number }
  company?: { id: string; businessName: string } | null
  appointments: Array<{ id: string; startsAt: string; status: string }>
  charges: Array<{ id: string; amount: number; status: string }>
}

const ORDER_STATUS: Record<string, { label: string; className: string }> = {
  CREATED: { label: 'Creada', className: 'bg-gray-100 text-gray-700' },
  SCHEDULED: { label: 'Agendada', className: 'bg-sky-100 text-sky-800' },
  IN_PROGRESS: { label: 'En proceso', className: 'bg-amber-100 text-amber-800' },
  COMPLETED: { label: 'Completada', className: 'bg-emerald-100 text-emerald-800' },
  CANCELLED: { label: 'Anulada', className: 'bg-red-100 text-red-700' },
}

const RESULT_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  FIT: 'Apto',
  FIT_WITH_RESTRICTIONS: 'Apto con restricciones',
  UNFIT: 'No apto',
}

export function PreocupacionalPage() {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const isAccountant = user?.role === 'ACCOUNTANT'
  const [error, setError] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [rut, setRut] = useState('')
  const [patient, setPatient] = useState<Patient | null>(null)
  const [form, setForm] = useState({ batteryId: '', payerType: 'COMPANY', companyId: '', jobTitle: '' })

  const orders = useQuery({
    queryKey: ['preoccupational-orders'],
    queryFn: async () => (await api.get<Order[]>('/preoccupational-orders')).data,
  })
  const batteries = useQuery({
    queryKey: ['batteries'],
    queryFn: async () => (await api.get<Battery[]>('/catalog/batteries')).data,
  })
  const companies = useQuery({
    queryKey: ['companies'],
    queryFn: async () => (await api.get<Company[]>('/companies')).data,
  })

  async function findPatient() {
    setError('')
    try {
      const { data } = await api.get(`/patients/by-rut/${encodeURIComponent(rut)}`)
      if (data.found) setPatient(data.patient)
      else setError('Paciente no encontrado: créalo primero desde el flujo de nueva cita.')
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  const createOrder = useMutation({
    mutationFn: async () =>
      (
        await api.post('/preoccupational-orders', {
          patientId: patient!.id,
          batteryId: form.batteryId,
          payerType: form.payerType,
          companyId: form.payerType === 'COMPANY' ? form.companyId : null,
          agreementId:
            form.payerType === 'COMPANY'
              ? companies.data?.find((c) => c.id === form.companyId)?.agreements.find((a) => a.isActive)?.id ?? null
              : null,
          jobTitle: form.jobTitle || null,
        })
      ).data,
    onSuccess: () => {
      setShowNew(false)
      setPatient(null)
      setRut('')
      setError('')
      queryClient.invalidateQueries({ queryKey: ['preoccupational-orders'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const updateOrder = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, string> }) =>
      (await api.patch(`/preoccupational-orders/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preoccupational-orders'] })
      queryClient.invalidateQueries({ queryKey: ['charges'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const invoice = useMutation({
    mutationFn: async (companyId: string) => (await api.post('/tax-documents/consolidated', { companyId })).data,
    onSuccess: (doc) => {
      setError('')
      alert(`Factura consolidada en borrador — folio interno #${doc.internalFolio} por ${clp(doc.totalAmount)}`)
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const canWrite = user && ['ADMIN', 'RECEPTION'].includes(user.role)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold text-ink">Exámenes preocupacionales</h1>
        {canWrite && (
          <Button variant="cta" onClick={() => setShowNew(true)}>+ Nueva orden</Button>
        )}
      </div>

      <ErrorText>{error}</ErrorText>

      <Card title="Órdenes">
        {orders.isLoading ? (
          <Spinner />
        ) : (orders.data?.length ?? 0) === 0 ? (
          <EmptyState message="Sin órdenes preocupacionales." />
        ) : (
          <Table headers={['Trabajador', 'Batería', 'Pagador', 'Cargo', 'Estado', 'Resultado', '']}>
            {(orders.data ?? []).map((o) => (
              <tr key={o.id}>
                <td className="px-2 py-2">
                  {o.patient.firstName} {o.patient.lastName}
                  <span className="block text-xs text-gray-400">{formatRut(o.patient.rut)}{o.jobTitle ? ` · ${o.jobTitle}` : ''}</span>
                </td>
                <td className="px-2 py-2">{o.battery.name}</td>
                <td className="px-2 py-2">{o.company ? (
                  <span className="inline-flex items-center gap-1.5"><Icon name="company" className="h-4 w-4 text-steel-500" />{o.company.businessName}</span>
                ) : 'Particular'}</td>
                <td className="px-2 py-2 text-xs text-gray-500">
                  {o.charges.length > 0 ? `${clp(o.charges[0].amount)} (${o.charges[0].status === 'PAID' ? 'pagado' : 'pendiente'})` : '—'}
                </td>
                <td className="px-2 py-2">
                  <Badge className={ORDER_STATUS[o.status].className}>{ORDER_STATUS[o.status].label}</Badge>
                </td>
                <td className="px-2 py-2 text-sm">
                  {RESULT_LABELS[o.result]}
                  {o.certificateCode && (
                    <span className="block font-mono text-xs text-gray-400" title="Código de verificación del certificado">
                      {o.certificateCode}
                    </span>
                  )}
                </td>
                <td className="px-2 py-2">
                  {canWrite && ['SCHEDULED', 'IN_PROGRESS', 'CREATED'].includes(o.status) && (
                    <div className="flex gap-1">
                      <Button
                        variant="secondary"
                        onClick={() => updateOrder.mutate({ id: o.id, data: { status: 'COMPLETED', result: 'FIT' } })}
                      >
                        Completar (apto)
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {isAccountant && (
        <Card title="Facturación consolidada a empresa">
          <div className="flex flex-wrap items-center gap-2">
            {(companies.data ?? []).map((c) => (
              <Button key={c.id} variant="secondary" disabled={invoice.isPending} onClick={() => invoice.mutate(c.id)}>
                Facturar pendientes de {c.businessName}
              </Button>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Agrupa en una factura borrador todos los cargos B2B no facturados de la empresa (emisión SII vía adaptador, stub en v1).
          </p>
        </Card>
      )}

      {showNew && (
        <Modal title="Nueva orden preocupacional" onClose={() => setShowNew(false)}>
          <div className="space-y-3">
            <Field label="RUT del trabajador">
              <div className="flex gap-2">
                <Input value={rut} onChange={(e) => setRut(e.target.value)} placeholder="12.345.678-5" />
                <Button onClick={findPatient} disabled={!rut}>Buscar</Button>
              </div>
            </Field>
            {patient && (
              <>
                <p className="rounded-lg bg-soft px-3 py-2 text-sm">
                  <strong>{patient.firstName} {patient.lastName}</strong> · {formatRut(patient.rut)}
                </p>
                <Field label="Batería de exámenes">
                  <Select value={form.batteryId} onChange={(e) => setForm({ ...form, batteryId: e.target.value })}>
                    <option value="">Selecciona…</option>
                    {(batteries.data ?? []).map((b) => (
                      <option key={b.id} value={b.id}>{b.name} — {clp(b.basePrice)}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Pagador">
                  <Select value={form.payerType} onChange={(e) => setForm({ ...form, payerType: e.target.value })}>
                    <option value="COMPANY">Empresa (facturación consolidada)</option>
                    <option value="PATIENT">Particular</option>
                  </Select>
                </Field>
                {form.payerType === 'COMPANY' && (
                  <Field label="Empresa">
                    <Select value={form.companyId} onChange={(e) => setForm({ ...form, companyId: e.target.value })}>
                      <option value="">Selecciona…</option>
                      {(companies.data ?? []).map((c) => (
                        <option key={c.id} value={c.id}>{c.businessName}</option>
                      ))}
                    </Select>
                  </Field>
                )}
                <Field label="Cargo al que postula">
                  <Input value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} placeholder="p. ej. Maestro andamiero" />
                </Field>
                <ErrorText>{error}</ErrorText>
                <Button
                  variant="cta"
                  className="w-full"
                  disabled={!form.batteryId || (form.payerType === 'COMPANY' && !form.companyId) || createOrder.isPending}
                  onClick={() => createOrder.mutate()}
                >
                  Crear orden
                </Button>
                <p className="text-xs text-gray-500">Luego agenda los exámenes desde la Agenda vinculándolos a esta orden.</p>
              </>
            )}
            {!patient && <ErrorText>{error}</ErrorText>}
          </div>
        </Modal>
      )}
    </div>
  )
}
